/**
 * SubSkill Mastery — SubSkill-level mastery computation for UI
 *
 * Computes mastery scores at the subSkill level, used by
 * skillAggregation.ts and various UI components.
 *
 * Extracted from adaptiveEngine.ts in O-2 (god-file split).
 */

import { type ModuleId, getModuleSubSkillQuestions } from '../content';
import { GroupId } from '../config/systemConfig';
import { QuestionState, SubSkillProgress } from './types';
import { FamilyMasteryState, getFamilyMasteryKey } from './familyMasteryEngine';
import { getQuestionStateKey, RECENT_ATTEMPT_WINDOW } from './questionAttemptHistory';
import { hasActiveDecay } from './masteryDecay';
export function computeSubSkillMastery(moduleId: ModuleId, subSkill: string, questionStates: Record<string, QuestionState>, group?: GroupId, familyMastery?: Record<string, FamilyMasteryState>,
/**
 * Reference timestamp for decay evaluation (F-MAS-R6-DECAY-NOW-PARAMETER-DRIFT,
 * mastery-RC audit). Threaded through to `hasActiveDecay` so every decay
 * check within one aggregation samples the SAME instant instead of each call
 * re-reading `Date.now()`. Defaults to `Date.now()` — runtime behaviour is
 * unchanged; this only restores the purity discipline of `masteryDecay.ts`.
 */
now: number = Date.now()): SubSkillProgress {
  let relevantQuestions = getModuleSubSkillQuestions(moduleId, subSkill);

  // Apply group filter if needed
  if (group !== undefined) {
    relevantQuestions = relevantQuestions.filter(q => q.group === group);
  }
  if (relevantQuestions.length === 0) {
    return {
      subSkill,
      masteryScore: 0,
      questionsAnswered: 0,
      correctCount: 0,
      trend: 'stable',
      lastPracticed: 0
    };
  }

  // 1. Calculate attempt stats from questionStates (still needed for counts/trend)
  let totalAttempts = 0;
  let correctAttempts = 0;
  let lastPracticed = 0;
  const allAttempts: {
    isCorrect: boolean;
    timestamp: number;
  }[] = [];
  relevantQuestions.forEach(q => {
    // Use group-scoped key to look up state
    const stateKey = group !== undefined ? getQuestionStateKey(q.id, group) : q.id;
    const state = questionStates[stateKey];
    if (state && state.attempts > 0) {
      totalAttempts += state.attempts;
      correctAttempts += state.correctAttempts;
      lastPracticed = Math.max(lastPracticed, state.lastAttemptDate);
      allAttempts.push(...state.history);
    }
  });

  // 2. Calculate mastery score
  let finalScore = 0;
  let hasDecay = false;
  if (familyMastery && group !== undefined) {
    // CANONICAL PATH: Use family mastery if available
    // Find unique families for this subSkill
    const familyIds = new Set<string>();
    relevantQuestions.forEach(q => {
      if (q.familyId) familyIds.add(q.familyId);
    });
    if (familyIds.size > 0) {
      let totalFamilyScore = 0;
      let attemptedFamilies = 0;
      let familyTotalAttempts = 0;
      let familyTotalCorrect = 0;
      let familyLastSeen = 0;
      const familyAllAttempts: {
        isCorrect: boolean;
        timestamp: number;
      }[] = [];
      familyIds.forEach(familyId => {
        const key = getFamilyMasteryKey(familyId, group);
        const state = familyMastery[key];
        if (state && state.attempts > 0) {
          totalFamilyScore += state.masteryScore;
          attemptedFamilies++;
          familyTotalAttempts += state.attempts;
          familyTotalCorrect += state.correct;
          familyLastSeen = Math.max(familyLastSeen, state.lastSeen);
          if (hasActiveDecay(state, now)) {
            hasDecay = true;
          }

          // Collect history for trend calculation
          state.history.forEach(h => {
            familyAllAttempts.push({
              isCorrect: h.isCorrect,
              timestamp: h.timestamp
            });
          });
        }
      });
      if (attemptedFamilies > 0) {
        finalScore = Math.round(totalFamilyScore / attemptedFamilies);

        // When questionStates has no real data (e.g. demo accounts with synthetic IDs),
        // derive counts from familyMastery so the UI correctly shows practiced skills
        if (totalAttempts === 0) {
          totalAttempts = familyTotalAttempts;
          correctAttempts = familyTotalCorrect;
          lastPracticed = familyLastSeen;
          // Use family history for trend calculation below
          allAttempts.push(...familyAllAttempts);
        }
      }
    }
  } else {
    // FALLBACK PATH: Use question attempt history (legacy logic)
    // Nieuwste eerst — daarom slice(0, N) hier en slice(-N) in
    // questionAttemptHistory: die array is chronologisch (oudste eerst).
    // Twee richtingen, dezelfde bedoeling; niet gelijktrekken zonder de
    // sorteerrichting mee te nemen.
    allAttempts.sort((a, b) => b.timestamp - a.timestamp);
    const recentWindow = allAttempts.slice(0, RECENT_ATTEMPT_WINDOW);
    if (recentWindow.length > 0) {
      let weightedScore = 0;
      let totalWeight = 0;
      recentWindow.forEach((attempt, index) => {
        const weight = 1 / (index + 1);
        weightedScore += (attempt.isCorrect ? 100 : 0) * weight;
        totalWeight += weight;
      });
      finalScore = Math.round(weightedScore / totalWeight);

      // Volume penalty
      if (totalAttempts < 5) {
        const volumeMultiplier = totalAttempts / 5;
        finalScore = Math.round(finalScore * volumeMultiplier);
      }
    }
  }

  // 3. Calculate trend
  let trend: 'improving' | 'stable' | 'struggling' = 'stable';
  allAttempts.sort((a, b) => b.timestamp - a.timestamp); // Ensure sorted
  const recentWindow = allAttempts.slice(0, RECENT_ATTEMPT_WINDOW);
  if (recentWindow.length >= 4) {
    const recentHalf = recentWindow.slice(0, Math.floor(recentWindow.length / 2));
    const olderHalf = recentWindow.slice(Math.floor(recentWindow.length / 2));
    if (recentHalf.length > 0 && olderHalf.length > 0) {
      const recentAvg = recentHalf.filter(a => a.isCorrect).length / recentHalf.length;
      const olderAvg = olderHalf.filter(a => a.isCorrect).length / olderHalf.length;
      if (recentAvg > olderAvg + 0.2) trend = 'improving';else if (recentAvg < olderAvg - 0.2) trend = 'struggling';
    }
  }
  return {
    subSkill,
    masteryScore: finalScore,
    questionsAnswered: totalAttempts,
    correctCount: correctAttempts,
    trend,
    lastPracticed,
    hasDecay
  };
}